import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useDisclosure } from "@mantine/hooks";
import {
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  TagsInput,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import { changeRuleStatusAdvancedSearch } from "../../services/rules";
import { getAxiosResponseError } from "../../utils/functions";

const PERIOD_ITEMS = [
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
  { value: "QUARTER", label: "Quarter" },
  { value: "YEAR", label: "Year" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaultValues = {
  name: "",
  fromDate: null,
  period: "",
  periodCount: 1,
  emailsOfReceivers: [],
};

const ActivateReportModal = ({ id, onSuccess }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [emailError, setEmailError] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues,
  });

  const activateMutation = useMutation({
    mutationFn: changeRuleStatusAdvancedSearch,
    onSuccess: () => {
      toast.success("Report has been Scheduled");
      onSuccess?.();
      handleClose();
    },
    onError: (err) => {
      toast.error(getAxiosResponseError(err));
    },
  });

  const handleClose = () => {
    reset(defaultValues);
    setEmailError(null);
    close();
  };

  const onSubmit = (values) => {
    activateMutation.mutate({
      id,
      date: dayjs(values.fromDate).format("YYYY-MM-DD"),
      status: "ACTIVATED",
      name: values.name,
      period: values.period,
      periodCount: values.periodCount,
      emailsOfReceivers: values.emailsOfReceivers.join(","),
    });
  };

  return (
    <>
      <Button variant="outline" onClick={open}>
        Activate report
      </Button>

      <Modal
        opened={opened}
        onClose={handleClose}
        title="Activate report"
        centered
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextInput {...field} label="Name" required />
              )}
            />

            <Controller
              name="fromDate"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <DateInput
                  {...field}
                  label="From date"
                  placeholder="Select date"
                  minDate={new Date()}
                  required
                />
              )}
            />

            <Group grow align="flex-start">
              <Controller
                name="period"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Period"
                    placeholder="Select period"
                    data={PERIOD_ITEMS}
                    required
                  />
                )}
              />

              <Controller
                name="periodCount"
                control={control}
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <NumberInput {...field} label="Count" min={1} required />
                )}
              />
            </Group>

            <Controller
              name="emailsOfReceivers"
              control={control}
              render={({ field }) => (
                <TagsInput
                  {...field}
                  label="Emails of receivers"
                  placeholder="Type an email and press Enter"
                  error={emailError}
                  onChange={(value) => {
                    const invalid = value.find((v) => !EMAIL_REGEX.test(v));
                    if (invalid) {
                      setEmailError(`Invalid email: ${invalid}`);
                      return;
                    }
                    setEmailError(null);
                    field.onChange(value);
                  }}
                  splitChars={[",", " ", ";"]}
                  clearable
                />
              )}
            />

            <Group justify="space-between" mt="sm">
              <Button color="red" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="green"
                variant="outline"
                disabled={!isValid}
                loading={activateMutation.isPending}
              >
                Confirm
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};

export default ActivateReportModal;
